import { useLayoutEffect, useRef, useState } from "react";

/**
 * Animated Delgentic wordmark. On load, the two source words fly in from the
 * right — "Delegating" and "Agentic" — then their non-name parts ("egating" /
 * "A") fade away and the remaining letter-groups merge onto the matching
 * letters of the black "Delgentic": Del → Del (emerald), gentic → gentic
 * (cyan). Those letters then stay coloured and gently pulse, signifying the
 * name's source: DELegating + aGENTIC.
 *
 * The merge uses a measured (FLIP) transform so each group lands exactly on its
 * glyphs. Existing animations are cancelled before measuring so React
 * StrictMode's dev double-invoke can't corrupt the distance. Honors
 * prefers-reduced-motion and skips the choreography on small screens.
 */
export function BrandWordmark() {
  const delTarget = useRef<HTMLSpanElement>(null);
  const genticTarget = useRef<HTMLSpanElement>(null);
  // "Delegating" = Del (merges) + egating (drops)
  const w1 = useRef<HTMLSpanElement>(null);
  const w1merge = useRef<HTMLSpanElement>(null);
  const w1drop = useRef<HTMLSpanElement>(null);
  // "Agentic" = A (drops) + gentic (merges)
  const w2 = useRef<HTMLSpanElement>(null);
  const w2merge = useRef<HTMLSpanElement>(null);
  const w2drop = useRef<HTMLSpanElement>(null);
  const [delLit, setDelLit] = useState(false);
  const [genticLit, setGenticLit] = useState(false);

  useLayoutEffect(() => {
    const skip =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(max-width: 767px)").matches;
    if (skip) {
      setDelLit(true);
      setGenticLit(true);
      return;
    }

    const els = [w1, w1merge, w1drop, w2, w2merge, w2drop]
      .map((r) => r.current)
      .filter(Boolean) as HTMLSpanElement[];
    // Reset any in-flight animation so re-measurement (StrictMode) is accurate.
    els.forEach((el) => el.getAnimations().forEach((a) => a.cancel()));

    const runWord = (
      word: HTMLSpanElement | null,
      merge: HTMLSpanElement | null,
      drop: HTMLSpanElement | null,
      target: HTMLSpanElement | null,
      startDelay: number,
      onArrive: () => void,
    ) => {
      if (!word || !merge || !drop || !target) return;
      // Phase 1 — the whole source word flies in from the right and fades in.
      const a1 = word.animate(
        [
          { transform: "translateX(38px)", opacity: 0, filter: "blur(1.5px)" },
          { transform: "translateX(0)", opacity: 1, filter: "blur(0)" },
        ],
        {
          duration: 500,
          delay: startDelay,
          easing: "cubic-bezier(0.22,1,0.36,1)",
          fill: "both",
        },
      );
      a1.onfinish = () => {
        // Phase 2 — the non-name part fades + shrinks away.
        const a2 = drop.animate(
          [
            { opacity: 1, transform: "scale(1)" },
            { opacity: 0, transform: "scale(0.6)" },
          ],
          { duration: 300, easing: "ease-in", fill: "forwards" },
        );
        a2.onfinish = () => {
          // Phase 3 — measure the merging group NOW and FLIP it onto its glyphs.
          const m = merge.getBoundingClientRect();
          const t = target.getBoundingClientRect();
          const dx = t.left - m.left;
          const dy = t.top - m.top;
          const a3 = merge.animate(
            [
              { transform: "translate(0,0)" },
              { transform: `translate(${dx}px,${dy}px)` },
            ],
            { duration: 560, easing: "cubic-bezier(0.4,0,0.2,1)", fill: "forwards" },
          );
          a3.onfinish = () => {
            onArrive(); // colourise the wordmark letters underneath…
            merge.animate([{ opacity: 1 }, { opacity: 0 }], {
              duration: 220,
              fill: "forwards",
            }); // …then fade the flyer to reveal them (seamless: same colour + place)
          };
        };
      };
    };

    runWord(w1.current, w1merge.current, w1drop.current, delTarget.current, 250, () =>
      setDelLit(true),
    );
    runWord(w2.current, w2merge.current, w2drop.current, genticTarget.current, 520, () =>
      setGenticLit(true),
    );

    return () => els.forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
  }, []);

  return (
    <span className="relative font-display text-[15px] font-semibold tracking-tight">
      <span ref={delTarget} className={`brand-seg brand-seg-del${delLit ? " is-lit" : ""}`}>
        Del
      </span>
      <span
        ref={genticTarget}
        className={`brand-seg brand-seg-gentic${genticLit ? " is-lit" : ""}`}
      >
        gentic
      </span>

      {/* Source-word flyers, same font as the wordmark so the merging groups
          overlay its glyphs exactly. Absolute + aria-hidden — the wordmark above
          is the real text. Hidden on small screens (handled statically there). */}
      <span
        className="pointer-events-none absolute left-full top-0 ml-2 hidden gap-2 md:inline-flex"
        aria-hidden="true"
      >
        <span ref={w1} className="inline-flex" style={{ opacity: 0 }}>
          <span ref={w1merge} className="font-semibold text-[color:var(--accent2)]">
            Del
          </span>
          <span ref={w1drop} className="font-semibold text-[color:var(--muted-ink)]">
            egating
          </span>
        </span>
        <span ref={w2} className="inline-flex" style={{ opacity: 0 }}>
          <span ref={w2drop} className="font-semibold text-[color:var(--muted-ink)]">
            A
          </span>
          <span ref={w2merge} className="font-semibold text-[color:var(--brand)]">
            gentic
          </span>
        </span>
      </span>
    </span>
  );
}
